/**
 * Single source of truth for currency formatting in the portal.
 *
 * The company's currency (symbol/code/locale) is resolved by the backend from
 * its registered country and returned on the auth `user.company`. This module
 * mirrors that value so that BOTH React components (via `useCurrency`) and
 * plain functions (via `formatCurrency`) render the correct currency — India
 * shows ₹, US shows $, etc. Never hardcode ₹/$/INR anywhere else.
 *
 * Keep COUNTRY_CURRENCY in sync with the backend `api/src/config/currency.ts`.
 */
import { useAuthStore } from '../store/authStore';

export interface CurrencyInfo {
  code: string;
  symbol: string;
  locale: string;
}

export const DEFAULT_CURRENCY: CurrencyInfo = { code: 'INR', symbol: '₹', locale: 'en-IN' };

export const COUNTRY_CURRENCY: Record<string, CurrencyInfo> = {
  in: { code: 'INR', symbol: '₹', locale: 'en-IN' },
  us: { code: 'USD', symbol: '$', locale: 'en-US' },
  gb: { code: 'GBP', symbol: '£', locale: 'en-GB' },
  ae: { code: 'AED', symbol: 'د.إ', locale: 'ar-AE' },
  sa: { code: 'SAR', symbol: '﷼', locale: 'ar-SA' },
  qa: { code: 'QAR', symbol: '﷼', locale: 'ar-QA' },
  om: { code: 'OMR', symbol: '﷼', locale: 'ar-OM' },
  bh: { code: 'BHD', symbol: '.د.ب', locale: 'ar-BH' },
  kw: { code: 'KWD', symbol: 'د.ك', locale: 'ar-KW' },
  my: { code: 'MYR', symbol: 'RM', locale: 'ms-MY' },
  sg: { code: 'SGD', symbol: '$', locale: 'en-SG' },
  au: { code: 'AUD', symbol: '$', locale: 'en-AU' },
  de: { code: 'EUR', symbol: '€', locale: 'de-DE' },
  fr: { code: 'EUR', symbol: '€', locale: 'fr-FR' },
  cn: { code: 'CNY', symbol: '¥', locale: 'zh-CN' },
  jp: { code: 'JPY', symbol: '¥', locale: 'ja-JP' },
  kr: { code: 'KRW', symbol: '₩', locale: 'ko-KR' },
  id: { code: 'IDR', symbol: 'Rp', locale: 'id-ID' },
  th: { code: 'THB', symbol: '฿', locale: 'th-TH' },
  lk: { code: 'LKR', symbol: 'Rs', locale: 'en-LK' },
  np: { code: 'NPR', symbol: 'रू', locale: 'ne-NP' },
  bd: { code: 'BDT', symbol: '৳', locale: 'bn-BD' },
  pk: { code: 'PKR', symbol: '₨', locale: 'en-PK' },
  za: { code: 'ZAR', symbol: 'R', locale: 'en-ZA' },
  ng: { code: 'NGN', symbol: '₦', locale: 'en-NG' },
  ke: { code: 'KES', symbol: 'KSh', locale: 'en-KE' },
  gh: { code: 'GHS', symbol: 'GH₵', locale: 'en-GH' },
  br: { code: 'BRL', symbol: 'R$', locale: 'pt-BR' },
  mx: { code: 'MXN', symbol: '$', locale: 'es-MX' },
  ru: { code: 'RUB', symbol: '₽', locale: 'ru-RU' },
  it: { code: 'EUR', symbol: '€', locale: 'it-IT' },
};

interface CompanyCurrencyLike {
  countryCode?: string | null;
  currencyCode?: string | null;
  currencySymbol?: string | null;
  currencyLocale?: string | null;
}

/** Resolve currency from a company object: prefer stored fields, fall back to country, then INR. */
export function resolveCompanyCurrency(company?: CompanyCurrencyLike | null): CurrencyInfo {
  if (!company) return DEFAULT_CURRENCY;
  if (company.currencySymbol && company.currencyCode && company.currencyLocale) {
    return { code: company.currencyCode, symbol: company.currencySymbol, locale: company.currencyLocale };
  }
  if (company.countryCode) {
    return COUNTRY_CURRENCY[company.countryCode.toLowerCase()] || DEFAULT_CURRENCY;
  }
  return DEFAULT_CURRENCY;
}

// ── Module mirror (for non-React callers) ──
// Kept in sync with the auth store so plain helper functions format correctly.
let activeCurrency: CurrencyInfo = resolveCompanyCurrency(useAuthStore.getState().user?.company);

useAuthStore.subscribe((state) => {
  activeCurrency = resolveCompanyCurrency(state.user?.company);
});

export function getActiveCurrency(): CurrencyInfo {
  return activeCurrency;
}

export function getCurrencySymbol(): string {
  return activeCurrency.symbol;
}

interface FormatOptions {
  /** Number of fraction digits (default 2). */
  fractionDigits?: number;
  /** If true, omit the symbol and return just the grouped number. */
  noSymbol?: boolean;
}

/**
 * Format an amount in the active company currency, e.g. "₹1,234.50" / "$1,234.50".
 * Safe for null/NaN (returns "<symbol>0").
 */
export function formatCurrency(
  amount: number | string | null | undefined,
  opts: FormatOptions = {},
): string {
  const { symbol, locale } = activeCurrency;
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (num === null || num === undefined || Number.isNaN(num)) {
    return opts.noSymbol ? '0' : `${symbol}0`;
  }
  const digits = opts.fractionDigits ?? 2;
  const formatted = num.toLocaleString(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return opts.noSymbol ? formatted : `${symbol}${formatted}`;
}
